import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CreateLead() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customer_name: '',
    mobile: '',
    email: '',
    projects: [],
    source: '',
    sub_source: '',
    assigned_to: 'auto-assign', // Default to auto-assignment for Super Admin
    campaign_id: '', // For social media campaigns
    entry_method: 'Manual entry', // Phase 1A: Default to Manual entry
    location: '',
    budget_range: '',
    property_type: '',
    comment: '' // Comment for lead creation
  });
  const [projects, setProjects] = useState([]);
  const [sources, setSources] = useState([]);
  const [subSources, setSubSources] = useState([]); // Sub-sources for selected source
  const [campaigns, setCampaigns] = useState([]); // Campaign list
  const [salesReps, setSalesReps] = useState([]); // Sales reps for Super Admin assignment
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true); // Loading state for initial data load

  const isAdminOrManager = user?.user_role === 'Super Administrator' || user?.user_role === 'Manager';

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    const token = localStorage.getItem('token');
    setLoadingData(true);
    try {
      const requests = [
        axios.get(`${API}/projects/assigned`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/campaigns`, { headers: { Authorization: `Bearer ${token}` } })
      ];

      // CHANGE 4: Use active brokers endpoint for assignment dropdown
      if (isAdminOrManager) {
        requests.push(axios.get(`${API}/brokers/active`, { headers: { Authorization: `Bearer ${token}` } }));
      }

      // Load sources from source-management API (dynamic from database)
      let sourcesData = [];
      try {
        // Try source-management API first (for Super Admin)
        const sourceManagementRes = await axios.get(`${API}/master/source-management`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Extract sources from the response structure: [{source: {value, label}, sub_sources: [...]}]
        sourcesData = sourceManagementRes.data.map(item => item.source).filter(s => s.is_active !== false);
      } catch (sourceError) {
        // Fallback to sources-with-sub-sources for non-Super Admin users
        if (sourceError.response?.status === 403) {
          try {
            const sourcesWithSubRes = await axios.get(`${API}/master/sources-with-sub-sources`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            // Extract sources from the response structure
            sourcesData = sourcesWithSubRes.data.map(item => item.source).filter(s => s.is_active !== false);
          } catch (fallbackError) {
            console.error('Failed to load sources:', fallbackError);
            // Final fallback to simple sources API
            const simpleSourcesRes = await axios.get(`${API}/master/sources`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            sourcesData = simpleSourcesRes.data || [];
          }
        } else {
          console.error('Failed to load sources:', sourceError);
          // Fallback to simple sources API
          try {
            const simpleSourcesRes = await axios.get(`${API}/master/sources`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            sourcesData = simpleSourcesRes.data || [];
          } catch (finalError) {
            console.error('All source APIs failed:', finalError);
          }
        }
      }

      const responses = await Promise.all(requests);
      const [projectsRes, campaignsRes, salesRepsRes] = responses;

      setProjects(projectsRes.data || []);
      setSources(sourcesData); // Sources extracted from source-management API
      setCampaigns(campaignsRes.data || []);
      
      // Set sales reps if available (only active ones)
      if (salesRepsRes) {
        setSalesReps(salesRepsRes.data || []);
      }
    } catch (error) {
      console.error('Failed to load master data:', error);
      toast.error('Failed to load data. Please refresh the page.');
      // Set empty arrays on error to prevent crashes
      setProjects([]);
      setSources([]);
      setCampaigns([]);
      setSalesReps([]);
    } finally {
      setLoadingData(false);
    }
  };

  // Load sub-sources based on selected source
  const loadSubSources = async (sourceName) => {
    if (!sourceName) {
      setSubSources([]);
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      // Try source-management API first (for Super Admin)
      let response;
      try {
        response = await axios.get(`${API}/master/source-management`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
      } catch (error) {
        // Fallback to sources-with-sub-sources for non-Super Admin users
        if (error.response?.status === 403) {
          response = await axios.get(`${API}/master/sources-with-sub-sources`, { 
            headers: { Authorization: `Bearer ${token}` } 
          });
        } else {
          throw error;
        }
      }
      
      // Find the selected source and get its sub_sources
      const selectedSource = response.data.find(source => source.source.value === sourceName);
      if (selectedSource && selectedSource.sub_sources) {
        setSubSources(selectedSource.sub_sources);
      } else {
        setSubSources([]);
      }
    } catch (error) {
      console.error('Error loading sub-sources:', error);
      setSubSources([]);
    }
  };

  // Handle source change
  const handleSourceChange = (value) => {
    setFormData({ ...formData, source: value, campaign_id: '', sub_source: '' });
    loadSubSources(value);
  };

  const handleProjectToggle = (projectName) => {
    setFormData(prev => ({
      ...prev,
      projects: prev.projects.includes(projectName)
        ? prev.projects.filter(p => p !== projectName)
        : [...prev.projects, projectName]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.mobile || !formData.source || formData.projects.length === 0) {
      toast.error('Please fill all required fields');
      return;
    }

    // Validate global mobile number (7-15 digits, E.164 format)
    const mobileDigits = formData.mobile.replace(/\D/g, '');
    if (mobileDigits.length < 7 || mobileDigits.length > 15) {
      toast.error('Mobile number must be between 7 and 15 digits');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      // MODIFICATION #8: If Sales Rep creates lead, assign to themselves
      const leadPayload = { ...formData };
      if (user?.user_role === 'Sales Rep') {
        leadPayload.assigned_to = user.id;
      } else if (leadPayload.assigned_to === 'auto-assign' || !leadPayload.assigned_to) {
        // Remove assigned_to field for auto-assignment (round-robin)
        delete leadPayload.assigned_to;
      }
      
      const response = await axios.post(`${API}/leads`, leadPayload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'X-Entry-Source': 'manual-form'
        }
      });
      
      const newLead = response.data;
      toast.success('Lead created successfully');
      
      // Add comment if provided
      if (formData.comment && formData.comment.trim()) {
        try {
          await axios.post(`${API}/leads/${newLead.id}/comments`, { 
            comment: formData.comment.trim() 
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (commentError) {
          // Don't fail the entire creation if comment fails
          console.error('Failed to add comment:', commentError);
          toast.warning('Lead created but comment failed to save');
        }
      }
      
      navigate('/leads');
    } catch (error) {
      if (error.response?.status === 400) {
        // Show the actual validation error from the backend
        const errorMessage = error.response?.data?.detail || 'Invalid lead data';
        toast.error(errorMessage);
      } else {
        toast.error('Failed to create lead');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="create-lead-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Add New Lead</h1>
              <p className="text-sm text-slate-600">Create a new lead entry</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4">
        <Card>
          <CardHeader>
            <CardTitle>Lead Information</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingData ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-slate-600">Loading form data...</p>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="customer_name">Customer Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    placeholder="Enter customer name"
                    required
                    data-testid="customer-name-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile Number <span className="text-red-500">*</span></Label>
                    <Input
                      id="mobile"
                      value={formData.mobile}
                      onChange={(e) => {
                        // Allow + prefix and digits, with optional formatting (spaces, dashes, parentheses)
                        let value = e.target.value;
                        // Remove all non-digit, non-formatting characters except + at start
                        value = value.replace(/[^\d+\s\-()]/g, '');
                        // Ensure + only appears at the start
                        if (value.includes('+') && !value.startsWith('+')) {
                          value = '+' + value.replace(/\+/g, '');
                        }
                        // Limit to 20 characters to allow for formatting
                        if (value.length <= 20) {
                          setFormData({ ...formData, mobile: value });
                        }
                      }}
                      placeholder="Enter mobile number (e.g., +1234567890 or 1234567890)"
                      required
                      data-testid="mobile-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email (Optional)</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@example.com"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Lead Source <span className="text-red-500">*</span></Label>
                    <Select value={formData.source} onValueChange={handleSourceChange}>
                      <SelectTrigger data-testid="source-select">
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {sources.length > 0 ? (
                          sources.map(source => (
                            <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>
                          ))
                        ) : (
                          <SelectItem value="" disabled>No sources available</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Show campaign dropdown only if source is Social Media */}
                  {formData.source === 'Social Media' && (
                    <div className="space-y-2">
                      <Label>Campaign (Optional)</Label>
                      <Select value={formData.campaign_id} onValueChange={(value) => setFormData({ ...formData, campaign_id: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select campaign" />
                        </SelectTrigger>
                        <SelectContent>
                          {campaigns
                            // .filter(c => formData.projects.includes(c.project_name))
                            .map(campaign => (
                              <SelectItem key={campaign.id} value={campaign.id}>
                                {campaign.campaign_name}
                                 {/* ({campaign.project_name}) */}
                              </SelectItem>
                            ))}
                              {campaigns?.length === 0 && (
                            <SelectItem value="none" disabled>No campaigns for selected projects</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Show sub_source dropdown only if NOT Social Media and sub-sources are available */}
                  {formData.source !== 'Social Media' && subSources.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="sub_source">Sub Source (Optional)</Label>
                      <Select value={formData.sub_source} onValueChange={(value) => setFormData({ ...formData, sub_source: value })}>
                        <SelectTrigger data-testid="sub-source-select">
                          <SelectValue placeholder="Select sub source" />
                        </SelectTrigger>
                        <SelectContent>
                          {subSources.map(subSource => (
                            <SelectItem key={subSource.value} value={subSource.value}>
                              {subSource.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Show sub_source text input only if NOT Social Media and no sub-sources in dropdown */}
                  {formData.source !== 'Social Media' && subSources.length === 0 && formData.source && (
                    <div className="space-y-2">
                      <Label htmlFor="sub_source">Sub Source (Optional)</Label>
                      <Input
                        id="sub_source"
                        value={formData.sub_source}
                        onChange={(e) => setFormData({ ...formData, sub_source: e.target.value })}
                        placeholder="e.g., Campaign name"
                        data-testid="sub-source-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Projects Selection */}
              <div className="space-y-3">
                <Label>Interested Projects <span className="text-red-500">*</span></Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-80 overflow-y-auto p-4 border border-slate-200 rounded-lg bg-slate-50">
                  {projects.map(project => (
                    <div key={project.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={project.id}
                        checked={formData.projects.includes(project.project_name)}
                        onCheckedChange={() => handleProjectToggle(project.project_name)}
                        data-testid={`project-checkbox-${project.id}`}
                      />
                      <label
                        htmlFor={project.id}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {project.project_name}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">{formData.projects.length} project(s) selected</p>
              </div>

              {/* Sales Rep Assignment (Super Admin only) */}
              {isAdminOrManager && (
                <div className="space-y-3">
                  <Label>Assign to Sales Rep (Optional)</Label>
                  <Select value={formData.assigned_to} onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}>
                    <SelectTrigger data-testid="sales-rep-select">
                      <SelectValue placeholder="Auto-assign (Round Robin)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto-assign">Auto-assign (Round Robin)</SelectItem>
                      {salesReps.map(rep => (
                        <SelectItem key={rep.id} value={rep.id}>
                          {rep.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {formData.assigned_to && formData.assigned_to !== 'auto-assign' ? 
                      `Lead will be assigned to ${salesReps.find(rep => rep.id === formData.assigned_to)?.full_name}` : 
                      'Lead will be auto-assigned using round-robin'
                    }
                  </p>
                </div>
              )}

              {/* Phase 1A: Additional Fields */}
              <div className="space-y-4 border-t border-slate-200 pt-4">
                <h3 className="font-semibold text-slate-700">Additional Information (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="City/Area"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="budget_range">Budget Range</Label>
                    <Input
                      id="budget_range"
                      value={formData.budget_range}
                      onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                      placeholder="e.g., 30-40 Lac"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="property_type">Property Type</Label>
                    <Input
                      id="property_type"
                      value={formData.property_type}
                      onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                      placeholder="e.g., 2 BHK"
                    />
                  </div>
                </div>
              </div>

              {/* Comment Field */}
              <div className="space-y-3">
                <Label htmlFor="comment">Comments (Optional)</Label>
                <Textarea
                  id="comment"
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  placeholder="Add any comments about this lead..."
                  className="min-h-[80px]"
                  rows={3}
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="submit-button"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {loading ? 'Creating...' : 'Create Lead'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-button">
                  Cancel
                </Button>
              </div>
            </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default CreateLead;
